import { calculateCommercialScore, getScoreReasons } from "./commercial.mjs";
import { todayParis } from "../utils/calendar.mjs";
import {
  getNextActionDate,
  importantNewSignal,
  latestSignal,
  getLastInteraction,
  absolutePriority,
  taskDueDate,
} from "./priority-policy.mjs";

function compare(a, b) {
  return (
    Number(!a.absolute) - Number(!b.absolute) ||
    (a.absolute?.rank ?? 99) - (b.absolute?.rank ?? 99) ||
    (a.absolute && b.absolute
      ? `${a.dueDate} ${a.task?.time || "23:59"}`.localeCompare(
          `${b.dueDate} ${b.task?.time || "23:59"}`,
        )
      : 0) ||
    b.score - a.score ||
    String(a.nextActionDate || "9999").localeCompare(
      String(b.nextActionDate || "9999"),
    ) ||
    String(a.client.id).localeCompare(String(b.client.id))
  );
}
export function getNextActions(
  state,
  date = todayParis(),
  excludeClientId = null,
  now = new Date(),
) {
  const actions = [];
  for (const client of state.clients) {
    if (client.closed) continue;
    const nextActionDate = getNextActionDate(client, state);
    const important = importantNewSignal(client, state, date);
    if (nextActionDate && nextActionDate > date && !important) continue;
    const tasks = state.tasks.filter(
      (t) => t.clientId === client.id && !t.done && t.category !== "manager",
    );
    const completed = state.results.some(
      (r) =>
        r.clientId === client.id && todayParis(new Date(r.createdAt)) === date,
    );
    const due = tasks.filter((t) => taskDueDate(t) && taskDueDate(t) <= date);
    const scoring = calculateCommercialScore(client, state, date);
    const base = {
      client,
      signal: important || latestSignal(client, state, date),
      score: scoring.score,
      reasons: getScoreReasons(client, state, date),
      rawScore: scoring.raw,
      nextActionDate,
      lastInteraction: getLastInteraction(client, state, date),
    };
    const candidates = due.map((task) => ({
      ...base,
      task,
      absolute: absolutePriority(task, client, state, date, now),
      dueDate: taskDueDate(task),
      title: task.title,
      label: ["call", "follow"].includes(task.category)
        ? "Saisir le résultat de mon appel"
        : "Commencer",
    }));
    const urgent = absolutePriority(null, client, state, date, now);
    if (
      !candidates.length &&
      urgent &&
      (!nextActionDate || nextActionDate <= date)
    )
      candidates.push({
        ...base,
        task: null,
        absolute: urgent,
        dueDate: date,
        title: "Traiter l’urgence client",
        label: "Saisir le résultat de mon appel",
      });
    const obligations = candidates.filter((a) => a.absolute).sort(compare);
    let action = obligations[0];
    if (!action && client.id !== excludeClientId) {
      if (completed && !important) continue;
      action = candidates.sort(compare)[0];
      if (!action && (!tasks.length || important))
        action = {
          ...base,
          task: null,
          absolute: null,
          dueDate: date,
          title: important
            ? "Qualifier le nouveau signal commercial"
            : "Premier contact",
          label: "Saisir le résultat de mon appel",
        };
    }
    if (!action) continue;
    action.whyNow =
      action.absolute?.why ||
      (important && nextActionDate > date
        ? "Nouveau signal important détecté après la programmation : qualification immédiate, rappel conservé"
        : "Meilleur score commercial parmi les établissements disponibles aujourd’hui");
    action.recommendedDate = action.task?.scheduled_date || date;
    actions.push(action);
  }
  return actions.sort(compare);
}
export function getNextBestAction(
  state,
  date = todayParis(),
  excludeClientId = null,
  now = new Date(),
) {
  return getNextActions(state, date, excludeClientId, now)[0] || null;
}
